import {
  PARTIES,
  PARTY_ORDER,
  type GlobalAggregate,
} from '../lib/types';
import { LATEST_YEAR, nationalShare, standingsNote } from '@shared/elections';

interface Props {
  totals: GlobalAggregate['totals'];
}

export function PartyStandings({ totals }: Props) {
  const claimed = PARTY_ORDER.reduce(
    (sum, p) => sum + (totals.districtsByParty[p] ?? 0),
    0,
  );
  const total = totals.totalDistricts || 1;
  const baselineShare = nationalShare(LATEST_YEAR);
  // Max share (live or baseline) anywhere — scale both bars to the same axis.
  const maxPct = Math.max(
    ...PARTY_ORDER.map(p => {
      const live = ((totals.districtsByParty[p] ?? 0) / total) * 100;
      const baseline = baselineShare[p];
      return Math.max(live, baseline);
    }),
    1,
  );

  return (
    <section className="section">
      <h2>Distrikt per parti</h2>
      <p className="updated-at">
        {claimed} av {totals.totalDistricts} distrikt erövrade ·{' '}
        {totals.neutralDistricts} neutrala
      </p>
      <p className="baseline-legend">
        <span className="baseline-swatch" /> ljus ram = riksdagsvalet {LATEST_YEAR}
      </p>
      {PARTY_ORDER.map((partyId, i) => {
        const count = totals.districtsByParty[partyId] ?? 0;
        const livePct = (count / total) * 100;
        const basePct = baselineShare[partyId];
        const note = standingsNote(partyId, { withYears: i === 0 });
        const liveWidth = (livePct / maxPct) * 100;
        const baseWidth = (basePct / maxPct) * 100;
        return (
          <div className="stat-row" key={partyId}>
            <span className="label">
              <span
                className="color-dot"
                style={{ background: PARTIES[partyId].color, marginRight: 8 }}
              />
              {partyId}
            </span>
            <span className="bar-track">
              <span
                className="bar-baseline"
                style={{
                  width: `${baseWidth}%`,
                  borderColor: PARTIES[partyId].color,
                }}
                title={`Riksdagsvalet ${LATEST_YEAR}: ${standingsNote(partyId, { withYears: true })}`}
              />
              <span
                className="bar-fill"
                style={{ width: `${liveWidth}%`, background: PARTIES[partyId].color }}
              />
            </span>
            <span className="value">{count}</span>
            <span className="bar-change">{note}</span>
          </div>
        );
      })}
    </section>
  );
}
